/**
 * Group-lead write scope in the planner (#2043). The backend is the boundary: every shift
 * write outside the scope answers 403 SHIFT_WRITE_OUT_OF_SCOPE. This module only decides
 * what the planner offers: which blocks drag, which open with Edit and Delete, which groups
 * the create and edit dialogs list.
 *
 * A restricted scope (a group lead) writes a shift when its groups are non-empty and all
 * led, and its employee is in the lead's visible scope (or the lead sees every employee).
 * An open shift (#2040) has no employee, so its groups alone decide.
 */
import type { Shift } from '@/types/shifts';
import { isOpenShift } from './openShifts';

export interface ShiftWriteScope {
  /** Admins and shifts-absences holders who lead no group. */
  unrestricted: boolean;
  /** The led groups of a restricted caller. */
  groupIds: readonly string[];
  /** True when every employee may be planned. */
  allEmployees: boolean;
  /** The employees a restricted caller may plan. */
  employeeIds: readonly string[];
}

export const UNRESTRICTED_WRITE_SCOPE: ShiftWriteScope = {
  unrestricted: true,
  groupIds: [],
  allEmployees: true,
  employeeIds: [],
};

/** A restricted scope with no group: nothing is writable. The base for a lead's scope. */
export const EMPTY_WRITE_SCOPE: ShiftWriteScope = {
  unrestricted: false,
  groupIds: [],
  allEmployees: false,
  employeeIds: [],
};

/** Whether a shift with these groups lies inside the scope. */
export function scopeAllowsGroups(scope: ShiftWriteScope, groupIds: readonly string[]): boolean {
  if (scope.unrestricted) return true;
  if (groupIds.length === 0) return false;
  return groupIds.every((id) => scope.groupIds.includes(id));
}

/** Whether the scope may plan this employee. */
export function scopeAllowsEmployee(scope: ShiftWriteScope, employeeId: string | undefined): boolean {
  if (scope.unrestricted || scope.allEmployees) return true;
  return !!employeeId && scope.employeeIds.includes(employeeId);
}

/** The group ids of a shift. */
export function shiftGroupIds(shift: Pick<Shift, 'groups'>): string[] {
  return (shift.groups ?? []).map((g) => g.id ?? '').filter((id) => id !== '');
}

/** Whether the caller may edit, move or delete this shift. An open shift (#2040) has no employee; its groups decide. */
export function canWriteShift(scope: ShiftWriteScope, shift: Shift): boolean {
  if (!scopeAllowsGroups(scope, shiftGroupIds(shift))) return false;
  return isOpenShift(shift) || scopeAllowsEmployee(scope, shift.employee_id);
}
